import {i18n} from '@online-offer/i18n';
import {Separator} from 'platform/components';
import {
  Box,
  Heading,
  Hide,
  HStack,
  Icon,
  Image,
  Show,
  Stack,
  Text,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter, useLocale} from 'platform/locale';
import styled from 'styled-components';

import {isNil, isNotNil} from 'ramda';

import {Nullish, parseDate} from 'shared/web';

import type {CommonAddressResponseBody} from '../../api/types/onlineOfferTypes';
import {CardOo} from '../../components/CardOo/CardOo';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {buildGoogleCalendarUrl} from './utils/buildGoogleCalendarUrl';
import {buildGoogleMapsUrl} from './utils/buildGoogleMapsUrl';
import {formatTestDriveAddressLine} from './utils/formatTestDriveAddressLine';
import {formatTestDriveTimeRange} from './utils/formatTestDriveTimeRange';
import {formatWeekday} from './utils/formatWeekday';

const MAP_PLACEHOLDER_SRC = '../../assets/images/map-placeholder.svg';
const TEST_DRIVE_DURATION_MS = 60 * 60 * 1000;

const MapLink = styled.a`
  display: block;
  position: relative;
  height: ${({theme}) => theme.getSize(37)};
  overflow: hidden;
  text-decoration: none;
  user-select: none;

  @media ${({theme}) => theme.devices.mobile} {
    height: ${({theme}) => theme.getSize(30)};
  }
`;

const MapPin = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -100%);
`;

const CalendarLink = styled.a`
  display: inline-block;
  text-decoration: none;
  user-select: none;
`;

type TestDriveLocationProps = {
  address: CommonAddressResponseBody | Nullish;
};

function TestDriveLocation(props: TestDriveLocationProps) {
  if (isNil(props.address)) {
    return null;
  }

  const addressLine = formatTestDriveAddressLine(props.address);

  return (
    <HStack spacing={2} align="flex-start">
      <Hide onMobile>
        <Icon value="testDriveLocation" size={4} color="text.tertiary" isNotFilled />
      </Hide>
      <Text size="small" color="secondary">
        {addressLine}
      </Text>
    </HStack>
  );
}

function TestDriveMap(props: TestDriveLocationProps) {
  if (isNil(props.address)) {
    return null;
  }

  return (
    <MapLink href={buildGoogleMapsUrl(props.address)} target="_blank" rel="noopener noreferrer">
      <Image src={MAP_PLACEHOLDER_SRC} alt="" width="100%" height="100%" fit="cover" />
      <MapPin aria-hidden>
        <Icon value="testDriveMapPin" size={[6, 7]} color="accent.base" />
      </MapPin>
      <Box
        position="absolute"
        bottom={[2, 3]}
        left={[2, 3]}
        backgroundColor="fill.white"
        borderRadius="border_radius_full"
        paddingHorizontal={[2, 3]}
        paddingVertical={[1, 2]}
        boxShadow="elevation_2"
      >
        <Text size="xSmall" alternative color="link">
          {i18n.t('testDriveCard.openInGoogleMaps')}
        </Text>
      </Box>
    </MapLink>
  );
}

function CompletedTestDriveBadge() {
  return (
    <Box
      height={7}
      paddingHorizontal={2}
      backgroundColor="palettes.green.20.100"
      borderRadius="border_radius_full"
    >
      <HStack height="100%" spacing={1} align="center">
        <Icon value="action/check_circle" size={4} color="text.success" />
        <Text size="xSmall" alternative color="success">
          {i18n.t('testDriveCard.completed')}
        </Text>
      </HStack>
    </Box>
  );
}

type PlannedTestDriveFooterProps = {
  address: CommonAddressResponseBody | Nullish;
  start: Date;
  vehicleTitle: string | Nullish;
};

function PlannedTestDriveFooter(props: PlannedTestDriveFooterProps) {
  const end = parseDate(props.start.getTime() + TEST_DRIVE_DURATION_MS); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
  const addressLine = isNotNil(props.address)
    ? formatTestDriveAddressLine(props.address)
    : undefined;
  const eventTitle = isNotNil(props.vehicleTitle)
    ? i18n.t('testDriveCard.calendarEventTitleWithVehicle', {vehicle: props.vehicleTitle})
    : i18n.t('testDriveCard.calendarEventTitle');
  const calendarUrl = buildGoogleCalendarUrl({
    title: eventTitle,
    start: props.start,
    end,
    location: addressLine,
  });

  return (
    <>
      <Separator spacing={0} />
      <Box paddingHorizontal={[4, 5]} paddingVertical={4}>
        <CalendarLink href={calendarUrl} target="_blank" rel="noopener noreferrer">
          <HStack spacing={2} align="center">
            <Hide onMobile>
              <Icon value="testDriveCalendar" size={4} color="general.accent" isNotFilled />
            </Hide>
            <Text size="small" alternative color="link">
              {i18n.t('testDriveCard.addToCalendar')}
            </Text>
          </HStack>
        </CalendarLink>
      </Box>
    </>
  );
}

export function TestDriveCard() {
  const {testDrive, vehicle} = useGetOnlineOfferData();
  const {language} = useLocale();
  const formatDateTime = useDateTimeFormatter();

  if (isNil(testDrive)) {
    return null;
  }

  const isCompleted = testDrive.state === 'completed';
  const start = parseDate(testDrive.scheduledAt);
  const returnedAt = isNotNil(testDrive.returnedAt) ? parseDate(testDrive.returnedAt) : null;
  const displayedEnd = isCompleted
    ? returnedAt
    : parseDate(start.getTime() + TEST_DRIVE_DURATION_MS); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
  const shortDate = formatDateTime('dateShort', start);
  const formattedDate = `${formatWeekday(start, language)} ${shortDate}`;
  const timeRange = formatTestDriveTimeRange(start, displayedEnd, formatDateTime);

  return (
    <CardOo padding={0} backgroundColor="semantic.bg.subtle" overflow="hidden">
      <Box paddingHorizontal={[4, 5]} paddingTop={4} paddingBottom={[4, 5]}>
        <VStack spacing={[3, 4]} width="100%">
          <HStack spacing={3} align="center" justify="space-between">
            <HStack spacing={3} align="center">
              <Icon value="testDrivePlanned" size={[5, 6]} color="accent.base" isNotFilled />
              <Heading size={5} headingLevel={4}>
                {isCompleted
                  ? i18n.t('testDriveCard.completedTitle')
                  : i18n.t('testDriveCard.title')}
              </Heading>
            </HStack>
            <Show when={isCompleted}>
              <CompletedTestDriveBadge />
            </Show>
          </HStack>

          <Stack
            direction={['row', 'column']}
            spacing={[3, 1]}
            align={['baseline', 'flex-start']}
            wrap
          >
            <Show onMobile>
              <Heading size={4} headingLevel={5}>
                {shortDate}
              </Heading>
            </Show>
            <Hide onMobile>
              <Heading size={4} headingLevel={5}>
                {formattedDate}
              </Heading>
            </Hide>
            <Text size="small" alternative color="link">
              {timeRange}
            </Text>
          </Stack>

          <Show when={testDrive.branchAddress}>
            <TestDriveLocation address={testDrive.branchAddress} />
          </Show>
        </VStack>
      </Box>

      <Show when={testDrive.branchAddress}>
        <Separator spacing={0} />
        <TestDriveMap address={testDrive.branchAddress} />
      </Show>

      <Show when={!isCompleted}>
        <PlannedTestDriveFooter
          start={start}
          address={testDrive.branchAddress}
          vehicleTitle={vehicle?.title}
        />
      </Show>
    </CardOo>
  );
}
